import React, { useState } from 'react';
import { OptimizationResult } from '../types';

interface ResultsDisplayProps {
  result: OptimizationResult | null;
}

export const ResultsDisplay: React.FC<ResultsDisplayProps> = ({ result }) => {
  const [expandedPallets, setExpandedPallets] = useState<Set<number>>(new Set());
  const [showAllPallets, setShowAllPallets] = useState(false);

  if (!result) return null;

  const togglePalletExpansion = (palletIndex: number) => {
    const newExpanded = new Set(expandedPallets);
    if (newExpanded.has(palletIndex)) {
      newExpanded.delete(palletIndex);
    } else {
      newExpanded.add(palletIndex);
    }
    setExpandedPallets(newExpanded);
  };

  const displayedPallets = showAllPallets ? result.pallets : result.pallets.slice(0, 3);
  const hasMorePallets = result.pallets.length > 3;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">Optimization Results</h2>
      
      {/* Summary Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4 sm:mb-6">
        <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-lg">
          <div className="text-xl sm:text-2xl font-bold text-blue-600">{result.totalPallets}</div>
          <div className="text-sm text-gray-600">Total Pallets</div>
        </div>
        <div className="text-center p-3 sm:p-4 bg-green-50 rounded-lg">
          <div className="text-xl sm:text-2xl font-bold text-green-600">{result.efficiency.toFixed(1)}%</div>
          <div className="text-sm text-gray-600">Efficiency</div>
        </div>
        <div className="text-center p-3 sm:p-4 bg-purple-50 rounded-lg">
          <div className="text-xl sm:text-2xl font-bold text-purple-600">
            {result.pallets.reduce((sum, pallet) => 
              sum + pallet.layers.reduce((layerSum, layer) => layerSum + layer.boxes.length, 0), 0
            )}
          </div>
          <div className="text-sm text-gray-600">Total Boxes</div>
        </div>
      </div>

      {/* Pallet List */}
      <div className="space-y-3">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Pallet Details</h3>
          {hasMorePallets && (
            <button
              onClick={() => setShowAllPallets(!showAllPallets)}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              {showAllPallets ? 'Show Less' : `Show All (${result.pallets.length})`}
            </button>
          )}
        </div>

        <div className="max-h-96 overflow-y-auto space-y-3 pr-2">
          {displayedPallets.map((pallet, index) => {
            const isExpanded = expandedPallets.has(index);
            const actualIndex = showAllPallets ? index : index;
            
            return (
              <div key={pallet.id} className="border border-gray-200 rounded-lg bg-gray-50">
                {/* Pallet Header */}
                <div 
                  className="p-3 sm:p-4 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => togglePalletExpansion(actualIndex)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3">
                      <h4 className="font-semibold text-gray-900">Pallet {actualIndex + 1}</h4>
                      <div className="flex flex-col sm:flex-row sm:space-x-4 text-sm text-gray-600 space-y-1 sm:space-y-0">
                        <span>{pallet.totalHeight.toFixed(1)} cm height</span>
                        <span>{pallet.layers.length} layers</span>
                        <span>{pallet.remainingHeight.toFixed(1)} cm remaining</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {pallet.asins.length > 0 && (
                        <div className="flex space-x-1">
                          {pallet.asins.slice(0, 2).map((asin, asinIndex) => (
                            <span 
                              key={asinIndex} 
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium"
                              title={asin}
                            >
                              {asin.slice(-6)}
                            </span>
                          ))}
                          {pallet.asins.length > 2 && (
                            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full">
                              +{pallet.asins.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                      <svg 
                        className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
            </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 p-3 sm:p-4 bg-white">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* ASINs */}
                      {pallet.asins.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Product IDs:</p>
                          <div className="flex flex-wrap gap-2">
                            {pallet.asins.map((asin, asinIndex) => (
                              <span 
                                key={asinIndex} 
                                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium"
                              >
                                {asin}
                              </span>
              ))}
            </div>
                        </div>
                      )}
                      
                      {/* Layer Details */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Layer Details:</p>
                        <div className="space-y-1">
                          {pallet.layers.map((layer, layerIndex) => (
                            <div key={layerIndex} className="text-xs text-gray-600">
                              Layer {layerIndex + 1}: {layer.boxes.length} boxes ({layer.height} cm)
                              {layer.boxes[0]?.asin && (
                                <span className="text-blue-600 ml-2">Product ID: {layer.boxes[0].asin.slice(-6)}</span>
                              )}
          </div>
        ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Show More Button */}
        {hasMorePallets && !showAllPallets && (
          <div className="text-center pt-4">
            <button
              onClick={() => setShowAllPallets(true)}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              Show {result.pallets.length - 3} more pallets...
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 